import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

type AddressPayload = {
  sourceType?: "nbn" | "opticomm";
  streetNumber?: string;
  streetName?: string;
  streetType?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  countryCode?: "au";
};

type AddressSuggestion = {
  id?: string;
  description: string;
  provider?: string;
  address?: AddressPayload;
};

type NbnQualificationResult = {
  success?: boolean;
  addressDetail?: {
    formattedAddress?: string;
    techType?: string;
    serviceStatus?: string;
    serviceClass?: string;
    serviceClassDescription?: string;
    locationId?: string;
  };
  productOptions?: Array<{
    id?: string;
    name?: string;
    downloadSpeedMbps?: number;
    uploadSpeedMbps?: number;
  }>;
  errorMessage?: string;
};

const STREET_TYPE_MAP: Record<string, string> = {
  road: "RD",
  rd: "RD",
  street: "ST",
  st: "ST",
  avenue: "AVE",
  ave: "AVE",
  place: "PL",
  pl: "PL",
  drive: "DR",
  dr: "DR",
  court: "CT",
  ct: "CT",
  crescent: "CRES",
  cres: "CRES",
  lane: "LANE",
  way: "WAY",
  parade: "PDE",
  pde: "PDE",
  terrace: "TCE",
  tce: "TCE",
  close: "CL",
  cl: "CL",
};

function splitStreet(street = "") {
  const words = street.trim().split(/\s+/).filter(Boolean);
  const possibleType = words[words.length - 1]?.toLowerCase();
  const streetType = STREET_TYPE_MAP[possibleType] || "";

  return {
    streetName: streetType ? words.slice(0, -1).join(" ") : street,
    streetType,
  };
}

function getTechnologyLabel(techType: string) {
  const tech = techType.toUpperCase();

  if (tech === "FW" || tech.includes("WIRELESS")) return "Fixed Wireless";
  if (tech.includes("OPTICOMM")) return "Opticomm";

  return techType || "Unknown";
}

function isLotAddress(value = "") {
  return /^LOT\s+\S+/i.test(value.trim()) || /\bLOT\s+\d+\b/i.test(value);
}

function sortStreetAddressesFirst(list: AddressSuggestion[]) {
  return [...list].sort((a, b) => Number(isLotAddress(a.description)) - Number(isLotAddress(b.description)));
}

function uniqueSuggestions(list: AddressSuggestion[]) {
  const seen = new Set<string>();
  return list.filter((suggestion) => {
    const key = `${suggestion.id || ""}-${suggestion.description}`.toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildNbnFallbackQueries(suggestion: AddressSuggestion) {
  const address = suggestion.address;
  if (!address) return [];

  const fullStreet = [address.streetNumber, address.streetName, address.streetType].filter(Boolean).join(" ");
  const streetWithoutType = [address.streetNumber, address.streetName].filter(Boolean).join(" ");
  const locality = [address.suburb, address.state, address.postcode].filter(Boolean).join(" ");
  const streetOnlyLocality = [address.streetName, address.suburb, address.state, address.postcode].filter(Boolean).join(" ");

  return [
    suggestion.description,
    [fullStreet, locality].filter(Boolean).join(" "),
    [streetWithoutType, locality].filter(Boolean).join(" "),
    streetOnlyLocality,
    [address.streetName, address.suburb, address.postcode].filter(Boolean).join(" "),
  ].filter(Boolean);
}

export default function NbnSearchBar(): JSX.Element {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<AddressSuggestion | null>(null);
  const [checkingNbn, setCheckingNbn] = useState(false);
  const [nbnResult, setNbnResult] = useState<NbnQualificationResult | null>(null);
  const [nbnError, setNbnError] = useState<string | null>(null);

  const navigate = useNavigate();
  const debounceRef = useRef<number | null>(null);
  const requestSeqRef = useRef(0);
  const GEO_KEY = process.env.REACT_APP_GEOAPIFY_KEY;

  const NBN_LOOKUP_ERROR =
    "The NBN availability service is currently unavailable. Please try again later or contact Jortel for help checking your address.";

  const goToPlansSmooth = (variant: string) => {
    navigate("/plans/nbn", { state: { nbnType: variant } });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 120);
  };

  const handleViewPlans = (tech: string) => {
    const t = tech.toUpperCase();

    if (t.includes("OPTICOMM")) goToPlansSmooth("opticomm");
    else if (t.includes("WIRELESS") || t === "FW") goToPlansSmooth("fw");
    else if (t.includes("HFC")) goToPlansSmooth("hfc");
    else if (t.includes("FTTP")) goToPlansSmooth("fttp");
    else goToPlansSmooth("fttb");

    setNbnResult(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedSuggestion(null);
    setQuery("");
  };

  const fetchGeoSuggestions = async (text: string): Promise<AddressSuggestion[]> => {
    if (!GEO_KEY) return [];

    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
      text
    )}&filter=countrycode:au&limit=6&apiKey=${GEO_KEY}`;

    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error("Address suggestions unavailable.");
    }

    return (data?.features || []).map((feature: any) => {
      const props = feature.properties || {};
      const street = splitStreet(props.street || props.address_line1 || "");

      return {
        id: props.place_id || props.formatted,
        description: props.formatted,
        provider: "geoapify",
        address: {
          streetNumber: props.housenumber || props.house_number,
          streetName: street.streetName,
          streetType: street.streetType,
          suburb: props.suburb || props.city || props.town || props.locality,
          state: props.state_code || props.state,
          postcode: props.postcode,
          countryCode: "au",
        },
      };
    });
  };

  const searchTelcoinaboxAddresses = async (payload: AddressPayload | { query: string }): Promise<AddressSuggestion[]> => {
    const response = await fetch("/api/nbn-address-search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Address search failed.");
    }

    return Array.isArray(data?.results) ? data.results : [];
  };

  const fetchSuggestions = async (text: string) => {
    const requestSeq = ++requestSeqRef.current;

    try {
      let list = GEO_KEY ? await fetchGeoSuggestions(text) : [];

      if (list.length === 0) {
        const telcoinaboxResults = await searchTelcoinaboxAddresses({ query: text });
        const userTypedLot = /\blot\b/i.test(text);
        list = userTypedLot
          ? sortStreetAddressesFirst(telcoinaboxResults)
          : sortStreetAddressesFirst(telcoinaboxResults).filter((suggestion) => !isLotAddress(suggestion.description));

        if (list.length === 0) {
          list = sortStreetAddressesFirst(telcoinaboxResults);
        }
      }

      if (requestSeq !== requestSeqRef.current) {
        return;
      }

      setSuggestions(list.slice(0, 6));
      setShowSuggestions(list.length > 0);
    } catch {
      if (requestSeq !== requestSeqRef.current) {
        return;
      }

      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedSuggestion(null);
    setNbnResult(null);
    setNbnError(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 3) {
      requestSeqRef.current += 1;
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = window.setTimeout(() => fetchSuggestions(value), 150);
  };

  const checkNbnAvailability = async (locationId: string) => {
    setCheckingNbn(true);

    try {
      const response = await fetch("/api/nbn-qualification-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locationId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || NBN_LOOKUP_ERROR);
      }

      setNbnResult(data);
      setNbnError(null);
    } catch (err: any) {
      setNbnError(err.message || NBN_LOOKUP_ERROR);
      setNbnResult(null);
    } finally {
      setCheckingNbn(false);
    }
  };

  const resolveAddressAndCheck = async (suggestion: AddressSuggestion) => {
    setCheckingNbn(true);
    setNbnError(null);
    setNbnResult(null);

    try {
      if (suggestion.id?.startsWith("LOC") || suggestion.id?.startsWith("OPC")) {
        await checkNbnAvailability(suggestion.id);
        return;
      }

      if (!suggestion.address) {
        throw new Error("Please select a full address suggestion.");
      }

      let matches = await searchTelcoinaboxAddresses({ query: suggestion.description });

      if (matches.length === 0) {
        matches = await searchTelcoinaboxAddresses(suggestion.address);
      }

      if (matches.length === 0) {
        const fallbackQueries = buildNbnFallbackQueries(suggestion);
        const fallbackResults: AddressSuggestion[] = [];

        for (const fallbackQuery of fallbackQueries) {
          try {
            const results = await searchTelcoinaboxAddresses({ query: fallbackQuery });
            fallbackResults.push(...results);
          } catch {
            // Try the next query shape.
          }
        }

        matches = uniqueSuggestions(sortStreetAddressesFirst(fallbackResults));
      }

      if (matches.length === 0) {
        setNbnError("We could not confirm that address online. Please check the spelling, select an NBN suggestion if one appears, or contact Jortel and we can check it manually.");
        return;
      }

      if (matches.length > 1) {
        setSuggestions(sortStreetAddressesFirst(matches).slice(0, 6));
        setShowSuggestions(true);
        setNbnError("Please select the matching NBN or Opticomm service address from the suggestions. Some official service records may use a lot number.");
        return;
      }

      await checkNbnAvailability(matches[0].id || "");
    } catch (err: any) {
      setNbnError(err.message || "Address search failed.");
    } finally {
      setCheckingNbn(false);
    }
  };

  const handleSelect = (suggestion: AddressSuggestion) => {
    setQuery(suggestion.description);
    setSelectedSuggestion(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    resolveAddressAndCheck(suggestion);
  };

  const handleSearch = async () => {
    if (selectedSuggestion) {
      resolveAddressAndCheck(selectedSuggestion);
      return;
    }

    if (!query.trim()) return;

    setNbnError("Please select a full address from the suggestions before searching.");
  };

  const address = nbnResult?.addressDetail;
  const techType = address?.techType || "";
  const technologyLabel = getTechnologyLabel(techType);
  const hasTechnology = Boolean(techType);
  const isAvailable = Boolean(nbnResult?.success || hasTechnology);
  const serviceStatus = isAvailable ? "Serviceability found" : address?.serviceStatus || "Unable to confirm online";
  const displayAddress =
    selectedSuggestion?.provider === "geoapify" && isLotAddress(address?.formattedAddress || "")
      ? selectedSuggestion.description
      : address?.formattedAddress || query;

  return (
    <>
      <h2 className="section-title">Check NBN Availability</h2>
      <p className="muted">Type your address and select a suggestion. Then hit Search.</p>

      <div className="nbn-search-wrap">
        <div className="nbn-search-bar">
          <input
            type="text"
            value={query}
            placeholder="Start typing address (e.g. 256 New Line Rd)"
            onChange={handleChange}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          />

          <button onClick={handleSearch} disabled={!query.trim() || checkingNbn}>
            {checkingNbn ? "Checking..." : "Search"}
          </button>
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <ul className="suggestions">
            {suggestions.map((suggestion) => (
              <li key={`${suggestion.id}-${suggestion.description}`} onClick={() => handleSelect(suggestion)}>
                {suggestion.description}
              </li>
            ))}
          </ul>
        )}
      </div>

      {nbnError && (
        <div style={{ marginTop: 12, background: "#fff1f1", padding: 12, borderRadius: 8, color: "#c53030" }}>
          {nbnError}
        </div>
      )}

      {nbnResult && (
        <div className="nbn-result-box">
          <div className="nbn-result-header">
            <div>
              <span className="nbn-result-kicker">Availability result</span>
              <h4>NBN Availability</h4>
            </div>
            <span className={`nbn-status-pill ${isAvailable ? "available" : "review"}`}>
              {serviceStatus}
            </span>
          </div>

          <div className="nbn-result-details">
            <div className="nbn-result-row wide">
              <span>Address</span>
              <strong>{displayAddress}</strong>
            </div>
            <div className="nbn-result-row">
              <span>Technology</span>
              <strong>{technologyLabel}</strong>
            </div>
          </div>

          {techType && (
            <div className="nbn-result-actions">
              <button onClick={() => handleViewPlans(techType)} className="view-plans-btn">
                View {technologyLabel} Plans
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
